import { createSlice, PayloadAction, createAsyncThunk } from '@reduxjs/toolkit';
import Config from 'config';
import axios, { AxiosResponse } from 'axios';
import { stringify } from 'qs';
import { obj2Arr, parseXMLRequestResponse } from '../utils/util';
import { TRequestError } from './types';
import {
  TComponentSubmitValues,
  TComponentValidatorObj,
  TComponentValidatorObjEvents,
  TFrmParent,
} from '../types';
import Field from '../JSApi/constants/Field';
import ApiaApi from '../api/apiaApi/apiaApi';
import propsStore from './propsStore/propsStore';

export type TRequestStatus = 'fulfilled' | 'pending' | 'rejected';

export type TStateValues = string | number | boolean | string[] | undefined;

export type TApiaFieldValuesObj<ValueType = TComponentSubmitValues> = {
  id?: string;
  isSynced?: boolean;
  valueState?: TComponentValidatorObj<ValueType>;
  attId?: string;
};

type TValuesSlice = IFormMounted & IValuesSlice;

export interface IFormMounted {
  mountedFields: string[];
}
export interface IValuesSlice {
  [key: string]: TApiaFieldValuesObj | null;
}

export type TProcessSubmitFieldResponse = {
  success: boolean;
};

export type TProcessAllSubmitFieldResponse = {
  success?: boolean;
};

export type TProcessSubmitAllPayload = {
  payload: {
    success?: boolean;
  };
  meta?: {
    arg: TProcessSubmitFieldParams;
    requestId: string;
    requestStatus: TRequestStatus;
    aborted?: boolean;
    condition?: boolean;
    rejectedWithValue?: boolean;
  };
  type: string;
};

export type TProcessSubmitFieldParams = {
  id: string;
  params: {
    attId: string;
    value?: TComponentSubmitValues;
    frmId: string;
    index: number;
    frmParent: TFrmParent;
    timestamp: number;
    clearValues?: boolean;
  };
};

const updateEventValue = (field: TApiaFieldValuesObj | null) => {
  if (field) {
    return {
      ...field,
      valueState: {
        ...field.valueState,
        value: field.valueState?.value,
        event: null,
      },
    };
  }
  return {};
};

const updateMountedFormComponentsList = (
  currentValue: TApiaFieldValuesObj,
  valState: TValuesSlice,
): string[] => {
  if (
    currentValue.isSynced &&
    currentValue?.id &&
    currentValue.valueState?.fieldType !== 'form'
  ) {
    const filteredFormIds = valState.mountedFields.filter(
      (el) => el !== currentValue.id,
    );
    filteredFormIds.push(currentValue?.id);
    return filteredFormIds;
  }
  return valState.mountedFields;
};

const getFieldSubmitParams = (
  field: TApiaFieldValuesObj,
): TProcessSubmitFieldParams | null => {
  const { id, valueState } = field;
  const propsState = propsStore.getAllFields();
  const propsStateObj = obj2Arr(propsState);

  for (let i = 0; i < propsStateObj.length; i += 1) {
    const props = propsStateObj[i];
    if (id && props?.id === id) {
      let processSubmitParams: TProcessSubmitFieldParams;
      let value = valueState?.value;
      if (
        // checkbox needs conversion if numeric before submit
        valueState?.fieldType === Field.TYPE_CHECK &&
        valueState?.valueType === 'N'
      ) {
        value = valueState?.value ? '1' : '0';
      }
      if (props?.attributes && props?.formData) {
        processSubmitParams = {
          id,
          params: {
            attId: props?.attributes?.attId,
            value,
            frmId: props?.formData?.frmId,
            index: props?.attributes?.index,
            frmParent: props?.formData?.frmParent,
            timestamp: Date.now(),
          },
        };
        if (
          valueState?.fieldType === Field.TYPE_MULTIPLE ||
          valueState?.fieldType === Field.TYPE_TREE ||
          valueState?.fieldType === Field.TYPE_EDITOR ||
          valueState?.fieldType === Field.TYPE_AREA
        ) {
          processSubmitParams = {
            id,
            params: {
              ...processSubmitParams.params,
              clearValues: true,
            },
          };
        }
        return processSubmitParams;
      }
    }
  }
  return null;
};

export const processFieldSubmit = createAsyncThunk<
  TProcessSubmitFieldResponse,
  TProcessSubmitFieldParams
>(
  'valuesSlice/processFieldSubmit',
  async (params: TProcessSubmitFieldParams) => {
    const response = await ApiaApi.post<TProcessSubmitFieldResponse>(
      Config.PROCESS_FIELD_SUBMIT_URL,
      {
        queryData: params.params,
        stringifyOptions: { indices: false },
      },
    );
    return response?.data as TProcessSubmitFieldResponse;
  },
);

export const processAllOutOFSyncFieldsSubmit = createAsyncThunk<
  TProcessSubmitAllPayload | null,
  (TApiaFieldValuesObj | null)[]
>(
  'valuesSlice/processAllOutOFSyncFieldsSubmit',
  async (fields: (TApiaFieldValuesObj | null)[], thunkAPI) => {
    try {
      const axiosArray: Promise<AxiosResponse>[] = [];
      let successIndex = 0;
      fields.forEach((field) => {
        if (field !== null && field?.attId) {
          const params = getFieldSubmitParams(field);
          const newPromise = axios.post(
            Config.PROCESS_FIELD_SUBMIT_URL,
            stringify(params?.params, { indices: false }),
          );
          axiosArray.push(newPromise);
        }
      });
      const response = await axios.all(axiosArray);
      for (let i = 0; i < response.length; i += 1) {
        const processSubmitFldResp = parseXMLRequestResponse(
          response[i],
        ) as TProcessAllSubmitFieldResponse;
        if (processSubmitFldResp.success === true) {
          successIndex += 1;
          if (successIndex === axiosArray.length) {
            return processSubmitFldResp as TProcessSubmitAllPayload;
          }
        } else {
          return processSubmitFldResp as TProcessSubmitAllPayload;
        }
      }
      return null;
    } catch (err) {
      const error = (err as TRequestError).response;
      return thunkAPI.rejectWithValue(error.data);
    }
  },
);

const valuesSlice = createSlice({
  name: 'valuesSlice',
  initialState: {
    mountedFields: [],
  } as TValuesSlice,
  reducers: {
    updateValue: (
      state: TValuesSlice,
      action: PayloadAction<TApiaFieldValuesObj>,
    ) => {
      const { id } = action.payload;
      if (id) {
        state.mountedFields = updateMountedFormComponentsList(
          action.payload,
          state,
        );
        state[id] = {
          ...state[id],
          ...action.payload,
        };
      }
    },
    updateValueEvent: (
      state: TValuesSlice,
      action: PayloadAction<TApiaFieldValuesObj>,
    ) => {
      const { id } = action.payload;
      if (id && state[id]) {
        state[id] = updateEventValue(state[id]);
      }
    },
    updateEvent(
      state: TValuesSlice,
      {
        payload: { event, id },
      }: PayloadAction<{ id: string; event: TComponentValidatorObjEvents }>,
    ) {
      const field = state[id];
      if (field?.valueState) {
        state[id] = {
          ...field,
          valueState: {
            ...field.valueState,
            event,
          },
        };
      }
    },
    removeValue: (
      state: TValuesSlice,
      action: PayloadAction<TApiaFieldValuesObj>,
    ) => {
      const { id } = action.payload;
      if (id) {
        state[id] = null;
      }
    },
  },
  extraReducers: (builder) => {
    builder.addCase(processFieldSubmit.pending, (state, action) => {
      const { id } = action.meta.arg;
      const fieldObj = state[id];
      if (fieldObj) fieldObj.isSynced = false;
    });
    builder.addCase(processFieldSubmit.fulfilled, (state, action) => {
      const { id } = action.meta.arg;
      const fieldObj = state[id];
      if (fieldObj) fieldObj.isSynced = true;
    });
    builder.addCase(processFieldSubmit.rejected, (state, action) => {
      const { id } = action.meta.arg;
      const fieldObj = state[id];
      if (fieldObj) fieldObj.isSynced = false;
    });

    builder.addCase(
      processAllOutOFSyncFieldsSubmit.pending,
      (state, action) => {
        if (Array.isArray(action.meta.arg)) {
          const fields = action.meta.arg;
          for (let i = 0; i < fields.length; i += 1) {
            const field = fields[i];
            const id = field?.id;
            if (id) {
              const fieldObj = state[id];
              if (fieldObj) fieldObj.isSynced = false;
            }
          }
        }
      },
    );
    builder.addCase(
      processAllOutOFSyncFieldsSubmit.fulfilled,
      (state, action) => {
        if (Array.isArray(action.meta.arg)) {
          const fields = action.meta.arg;
          for (let i = 0; i < fields.length; i += 1) {
            const field = fields[i];
            const id = field?.id;
            if (id) {
              const fieldObj = state[id];
              if (fieldObj) fieldObj.isSynced = true;
            }
          }
        }
      },
    );
    builder.addCase(
      processAllOutOFSyncFieldsSubmit.rejected,
      (state, action) => {
        if (Array.isArray(action.meta.arg)) {
          const fields = action.meta.arg;
          for (let i = 0; i < fields.length; i += 1) {
            const field = fields[i];
            const id = field?.id;
            if (id) {
              const fieldObj = state[id];
              if (fieldObj) fieldObj.isSynced = false;
            }
          }
        }
      },
    );
  },
});

export const { updateValue, removeValue, updateValueEvent, updateEvent } =
  valuesSlice.actions;
export default valuesSlice.reducer;
